import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { startScheduledClubSession } from '../../services/sessionManagementService';
import { fetchHostSelectStartWarning } from '../../utils/signupSelection';
import HostSelectStartConfirmModal from '../sessions/HostSelectStartConfirmModal';
import Button from './Button';

/**
 * Reusable component for session navigation buttons
 * Handles both "Enter Session" and "Start Session" actions with proper club-contexted routing
 */
const SessionNavigationButton = ({
  session,
  clubId,
  starterPlayerId,
  action = 'enter', // 'enter' or 'start'
  variant = 'primary',
  size = 'small',
  className = '',
  disabled = false,
  children,
  onStartSession, // Optional callback for custom start logic
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [startWarning, setStartWarning] = useState(null);

  const enterSession = () => {
    navigate(`/club/${clubId}/session/${session.id}`);
  };

  const startSession = async () => {
    if (onStartSession) {
      await onStartSession(session.id);
    } else if (starterPlayerId) {
      await startScheduledClubSession(session.id, starterPlayerId);
    } else {
      throw new Error('Missing starter player for session start');
    }
    enterSession();
  };

  const handleConfirmedStart = async () => {
    setLoading(true);
    try {
      await startSession();
      setStartWarning(null);
    } catch (error) {
      alert(`Failed to start session: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = async () => {
    if (!session?.id || !clubId) {
      console.error('SessionNavigationButton: Missing session.id or clubId');
      return;
    }

    if (action === 'start' && session.session_status !== 'ongoing') {
      setLoading(true);
      try {
        const warning = await fetchHostSelectStartWarning(session);
        if (warning) {
          setStartWarning(warning);
          return;
        }
        await startSession();
      } catch (error) {
        alert(`Failed to start session: ${error.message}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    enterSession();
  };

  const getButtonText = () => {
    if (children) return children;

    if (action === 'start') {
      return session?.session_status === 'upcoming' ? 'Start' : 'Enter';
    }

    return 'Enter';
  };

  const isDisabled = disabled || !session?.id || !clubId;

  return (
    <>
      <Button
        onClick={handleNavigation}
        variant={variant}
        size={size}
        className={className}
        disabled={isDisabled || loading}
      >
        {loading ? 'Starting...' : getButtonText()}
      </Button>
      <HostSelectStartConfirmModal
        warning={startWarning}
        loading={loading}
        onClose={() => {
          if (!loading) setStartWarning(null);
        }}
        onConfirm={handleConfirmedStart}
      />
    </>
  );
};

export default SessionNavigationButton;
